import React, { useEffect, useRef, useState } from "react";
import { currencyDisplay } from "../../../utils/helpers";
import { CruiseCabin, CruiseDeparture, CruiseRoomType } from "../../../interfaces/itinerary.interface";

const CARET_EDGE_INSET = 20;

const CruiseCabinSelector = (props: {
  departure?: CruiseDeparture;
  priceCurrency: string;
  selectedMultiplier?: number;
  selectedTourId?: number;
  onSelectRoomType: (roomType: CruiseRoomType) => void;
  onSelectCabin: (cabin: CruiseCabin) => void;
  cabinSelectionDisabled?: boolean;
}) => {
  const { departure, priceCurrency, selectedMultiplier, selectedTourId, onSelectRoomType, onSelectCabin, cabinSelectionDisabled } = props;

  const panelRef = useRef<HTMLDivElement>(null);
  const [caretLeft, setCaretLeft] = useState<number | null>(null);

  const selectedDate = departure?.departure_date;
  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;

    const update = () => {
      const card = document.querySelector<HTMLElement>(".tour-date-selector.cruise .date-box.selected");
      if (!card) {
        setCaretLeft(null);
        return;
      }
      const cardRect = card.getBoundingClientRect();
      const panelRect = panel.getBoundingClientRect();
      const centre = cardRect.left + cardRect.width / 2 - panelRect.left;
      setCaretLeft(Math.max(CARET_EDGE_INSET, Math.min(centre, panelRect.width - CARET_EDGE_INSET)));
    };

    update();

    const scroller = document.querySelector(".tour-date-selector.cruise .scroller");
    scroller?.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);

    return () => {
      scroller?.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [selectedDate]);

  if (!departure) return null;

  const roomTypes = departure.room_types || [];
  const selectedRoomType = roomTypes.find((rt) => rt.multiplier === selectedMultiplier);

  return (
    <div
      ref={panelRef}
      className="cruise-cabin-selector"
      style={caretLeft === null ? undefined : ({ "--cruise-caret-left": `${caretLeft}px` } as React.CSSProperties)}
    >
      <div className="cruise-cabin-selector_section">
        <span className="cruise-cabin-selector_label">Room Type</span>
        <div className="cruise-cabin-selector_pills cruise-cabin-selector_pills--rooms">
          {roomTypes.map((roomType) => {
            const isLoadingThisRoom = roomType.multiplier === selectedMultiplier && cabinSelectionDisabled;
            return (
            <button
              type="button"
              key={roomType.multiplier}
              disabled={cabinSelectionDisabled}
              className={`cruise-cabin-selector_pill ${roomType.multiplier === selectedMultiplier ? "selected" : ""} ${isLoadingThisRoom ? "loading" : ""}`}
              onClick={() => !cabinSelectionDisabled && onSelectRoomType(roomType)}
            >
              <span className="cruise-cabin-selector_pill-label">{roomType.label}</span>
            </button>
            );
          })}
        </div>
      </div>

      {selectedRoomType && (
        <div className="cruise-cabin-selector_section">
          <span className="cruise-cabin-selector_label">Please choose a cabin type</span>
          <div className="cruise-cabin-selector_pills cruise-cabin-selector_pills--cabins">
            {selectedRoomType.cabins.map((cabin) => {
              const selected = cabin.tour_id === selectedTourId;
              const isLoadingThisCabin = selected && cabinSelectionDisabled;
              return (
                <button
                  type="button"
                  key={cabin.tour_id}
                  disabled={!cabin.available || cabinSelectionDisabled}
                  className={`cruise-cabin-selector_pill ${selected ? "selected" : ""} ${!cabin.available ? "disabled" : ""} ${isLoadingThisCabin ? "loading" : ""}`}
                  onClick={() => cabin.available && !cabinSelectionDisabled && onSelectCabin(cabin)}
                >
                  <span className="cruise-cabin-selector_pill-label">{cabin.label}</span>
                  <span className="cruise-cabin-selector_pill-price">
                    {cabin.available ? `From ${currencyDisplay(priceCurrency, cabin.from_price)}` : "Sold out"}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default CruiseCabinSelector;
